/**
 * Host document URL / file name from Office common API (works Word on the web + desktop).
 * SharePoint / OneDrive URLs are exposed as `Office.context.document.url`; there is no separate
 * "title bar" API — parse the path segment you need from that URL.
 */

const LOG_PREFIX = "[hostDocumentInfo]";

/** Last non-empty path segment, URI-decoded (typical SharePoint file URL ends with `…/name.docx`). */
export function fileNameFromDocumentUrl(url: string | null | undefined): string | null {
  const raw = url?.trim();
  if (!raw) return null;
  try {
    const u = new URL(raw);
    const segments = u.pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1];
    if (!last) return null;
    return decodeURIComponent(last);
  } catch {
    return null;
  }
}

export function getHostDocumentUrlSync(): string | null {
  if (typeof Office === "undefined") return null;
  const u = Office.context?.document?.url?.trim();
  return u || null;
}

/**
 * Convert a SharePoint/OneDrive document URL into a backend-friendly remote path.
 *
 * Expected output format (example):
 * - `https://.../sites/tokio/Shared%20Documents/Generated%20Contract/foo.docx?...`
 *   → `tokio/Generated Contract/foo.docx`
 */
export function documentRemotePathFromDocumentUrl(url: string | null | undefined): string | null {
  const raw = url?.trim();
  if (!raw) return null;
  try {
    const u = new URL(raw);
    const segments = u.pathname.split("/").filter(Boolean).map(decodeURIComponent);
    const docLibIdx = segments.findIndex((s) => {
      const n = s.trim().toLowerCase();
      return n === "shared documents" || n === "documents";
    });
    if (docLibIdx < 0 || docLibIdx + 1 >= segments.length) {
      return null;
    }

    // Everything after the doc library is the remote path we want.
    const rest = segments.slice(docLibIdx + 1);

    // Ignore SharePoint UI routes if present.
    // e.g. ".../Forms/AllItems.aspx" should not be treated as a document path.
    if (rest.some((s) => s.trim().toLowerCase() === "forms")) {
      return null;
    }

    if (rest.length === 0) return null;
    return rest.join("/");
  } catch {
    return null;
  }
}

export async function getHostDocumentUrlAsync(): Promise<string | null> {
  if (typeof Office === "undefined") return null;
  await Office.onReady();
  const doc = Office.context?.document;
  if (!doc?.getFilePropertiesAsync) {
    return getHostDocumentUrlSync();
  }
  return new Promise((resolve) => {
    doc.getFilePropertiesAsync((result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        resolve(getHostDocumentUrlSync());
        return;
      }
      const u = result.value?.url?.trim();
      resolve(u || getHostDocumentUrlSync());
    });
  });
}

/** Dev aid: logs SharePoint-style document URL and derived file name after Office is ready. */
export async function logHostDocumentFileName(reason: string): Promise<void> {
  if (typeof Office === "undefined") return;
  await Office.onReady();
  const documentUrl = await getHostDocumentUrlAsync();
  const fileName = fileNameFromDocumentUrl(documentUrl);
  console.info(LOG_PREFIX, reason, {
    fileName,
    documentUrl,
    platform: Office.context?.platform,
    host: Office.context?.host
  });
}
